// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import {translate} from 'react-i18next';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import config from 'config';

// Modules
import * as mixpanelTracker from 'app-lib/mixpanel/mixpanelTracker';
import * as themeSelectorHelper from 'app-lib/helpers/theme-selector-helper';
const maxNumberOfSavedColors = 6;

export class ThemeSelector extends React.Component {
    constructor(props) {
        super(props);
        /*
        The base colors represent the color clicked on the colorWheel image or in the saved colors
        The display colors can be different from the base colors if the user selects a shade
        Both are saved as strings, such as "a6"
        */
        let red;
        let green;
        let blue;
        let hexInput;
        // If the user is using the default theme, the colors are set to a dark grey. Otherwise the original display colors come from initialColor
        if (!this.props.initialColor || this.props.initialColor === 'default') { // add condition to check if initial color exists
            red = '44';
            green = '44';
            blue = '44';
            hexInput = 'Default';
        } else {
            red = this.props.initialColor.substring(1, 3);
            green = this.props.initialColor.substring(3, 5);
            blue = this.props.initialColor.substring(5, 7);
            hexInput = `#${red}${green}${blue}`;
        }

        this.state = {
            baseColor: {
                red,
                green,
                blue
            },
            displayColor: {
                red,
                green,
                blue
            },
            savedColorsArr: {},
            selectorPosition: {
                left: '108px',
                top: '108px'
            },
            hexInput
        };
    }

    // Determines where the user clicks and then gets the color of that position on the colorWheel canvas
    handleOnClickColorWheel(event) {
        let canvas = this.refs.canvas;
        const rect = canvas.getBoundingClientRect();
        canvas = canvas.getContext('2d');
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // imgData is an array that stores RGB components of the place clicked on the canvas. The logic below
        // turns this from base 10 to base 16, and then appends a 0 to the front if the hex digit is length 1
        const imgData = canvas.getImageData(x, y, 1, 1).data;
        const red = imgData[0].toString(16).length === 1 ? `0${imgData[0].toString(16)}` : imgData[0].toString(16);
        const green = imgData[1].toString(16).length === 1 ? `0${imgData[1].toString(16)}` : imgData[1].toString(16);
        const blue = imgData[2].toString(16).length === 1 ? `0${imgData[2].toString(16)}` : imgData[2].toString(16);
        this.setState({
            baseColor: {
                red,
                green,
                blue
            },
            displayColor: {
                red,
                green,
                blue
            },
            selectorPosition: {
                left: `${x - 7}px`,
                top: `${y - 7}px`
            },
            hexInput: `#${red.toUpperCase()}${green.toUpperCase()}${blue.toUpperCase()}`
        }, () => {
            this.props.clickTheme(`#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}`);
        });
    }

    // Special handler for the default theme, which should be mostly a black page
    handleOnClickDefault(event) {
        // 44 is a dark grey, updates the UI to look similar to the default theme
        this.setState({
            baseColor: {
                red: '44',
                green: '44',
                blue: '44',
            },
            displayColor: {
                red: '44',
                green: '44',
                blue: '44'
            },
            selectorPosition: {
                left: '108px',
                top: '108px'
            },
            hexInput: 'Default'
        }, () => {
            this.props.clickTheme('default', event);
        });
    }

    // handler for when the user clicks on preset themes, saved themes, or shades
    handleOnClickColorBoxes(event) {
        // Saves event context for later
        const className = event.target.className;
        // Gets the background color of the box clicked.
        let currentBoxColors = document.defaultView.getComputedStyle(event.target, null).backgroundColor.slice(0, -1);
        currentBoxColors = currentBoxColors.slice(4);
        currentBoxColors = currentBoxColors.split(',');
        const red = this.toHex(currentBoxColors[0]).toUpperCase();
        const green = this.toHex(currentBoxColors[1]).toUpperCase();
        const blue = this.toHex(currentBoxColors[2]).toUpperCase();
        if (`#${red}${green}${blue}` === '#444444') {
            this.handleOnClickDefault();
            return;
        }
        this.setState({
            baseColor: {
                red: className !== 'theme-selector--grey-scale-box' ? red : this.state.baseColor.red,
                green: className !== 'theme-selector--grey-scale-box' ? green : this.state.baseColor.green,
                blue: className !== 'theme-selector--grey-scale-box' ? blue : this.state.baseColor.blue,
            },
            displayColor: {
                red,
                green,
                blue
            },
            selectorPosition: className !== 'theme-selector--grey-scale-box' ? {left: '108px', top: '108px'} : this.state.selectorPosition,
            hexInput: `#${red}${green}${blue}`
        }, () => {
            this.props.clickTheme(`#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}`);
        });
    }
    // Updates local storage to store the saved colors as a stringified json object
    updateSavedColors() {
        const saved = {};
        saved[0] = {red: this.state.displayColor.red, green: this.state.displayColor.green, blue: this.state.displayColor.blue};
        for (let i = 0; i < Math.min(Object.keys(this.state.savedColorsArr).length, maxNumberOfSavedColors - 1); i++) {
            saved[i + 1] = this.state.savedColorsArr[i];
        }

        localStorage.setItem('colorKeys', JSON.stringify(saved));
        this.setState({
            savedColorsArr: saved,
        });
    }
    hexInputOnKeyHandler(event) {
        if (event.keyCode === 13) {
            event.preventDefault();
            this.onHexFieldResolve();
        }
    }
    // Updates the state of the current user input.
    onHexFieldEnter(event) {
        this.setState({hexInput: event.target.value});
    }

    // Called when the user finishes entering a hexcode. Parses hexcode and then sets the state and updates UI accordingly
    onHexFieldResolve() {
        const hexInput = this.state.hexInput;
        if (hexInput.toLowerCase() === 'default') {
            this.handleOnClickDefault();
            return;
        }
        const parsedInput = this.parseHexInput(hexInput);
        if (parsedInput === '#444444') {
            this.handleOnClickDefault();
            return;
        }
        const red = parsedInput.substring(1, 3).toUpperCase();
        const green = parsedInput.substring(3, 5).toUpperCase();
        const blue = parsedInput.substring(5, 7).toUpperCase();
        this.setState({
            baseColor: {
                red,
                green,
                blue
            },
            displayColor: {
                red,
                green,
                blue
            },
            selectorPosition: {
                left: '108px',
                top: '108px'
            },
            hexInput: `#${red}${green}${blue}`
        }, () => {
            this.props.clickTheme(`#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}`);
        });
    }

    // Parses the input of the hex input. Discards non-hex characters, and adds 0's to end until reaches correct length. Does support #444 = #444444
    parseHexInput(hexInput) {
        let parsedInput = hexInput;
        parsedInput = parsedInput.replace(/[^0-9a-f]/gi, '');
        if (parsedInput.length === 3) {
            parsedInput = `${parsedInput.charAt(0)}${parsedInput.charAt(0)}${parsedInput.charAt(1)}${parsedInput.charAt(1)}${parsedInput.charAt(2)}${parsedInput.charAt(2)}`;
        }
        parsedInput = `#${parsedInput}`;
        while (parsedInput.length < 7) {
            parsedInput += '0';
        }
        return parsedInput;
    }

    // Converts a base 10 number to base 16. Numbers bigger than 255 become FF
    toHex(input) {
        let number = parseInt(input, 10);
        if (isNaN(number)) return '00';
        number = Math.max(0, Math.min(number, 255));
        return '0123456789ABCDEF'.charAt((number - number % 16) / 16) + '0123456789ABCDEF'.charAt(number % 16);
    }
    onOverlayEnter() {
        const saved = localStorage.getItem('colorKeys') ? JSON.parse(localStorage.getItem('colorKeys')) : {};
        this.setState({
            savedColorsArr: saved,
            selectorPosition: {
                left: '108px',
                top: '108px'
            }
        });
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.themeButtonCLicked}`);
    }
    loadCanvasImage() {
        const canvas = this.refs.canvas.getContext('2d');
        const img = new Image();
        img.src = `${config.assets.url}/images/catalog/color-wheel.png`;
        img.onload = () => canvas.drawImage(img, 0, 0);
    }

    // ------------------HTML Creation------------------------
    renderHeader() {
        const {t} = this.props;
        return (
            <div className="theme-selector--header">
                <div className="theme-selector--header-text">{t('channel.theme-selector.header')}</div>
            </div>
        );
    }

    renderSiteColors() {
        const {t} = this.props;
        const siteColors = [];
        themeSelectorHelper.siteColorsArray.forEach((item) => {
            let border = '';
            let outline = '';
            // Comparing the current display color to the current Site Color box
            if (item.toUpperCase() === (this.state.displayColor.red + this.state.displayColor.green + this.state.displayColor.blue).toUpperCase()) {
                outline = '2px solid #444444';
                border = '2px solid white';
            }
            const style = {
                backgroundColor: `#${item}`,
                border,
                outline
            };
            siteColors.push(
                <div
                  id={`static-${item}`}
                  key={item}
                  className={`theme-selector--color-box theme-selector--color-box-static theme-selector--color-box--static-${item}`}
                  onClick={this.handleOnClickColorBoxes.bind(this)}
                  style={style}
                />
            );
        });
        const style = {
            outline: `#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}` === '#444444' ? '2px solid #444444' : '',
            border: `#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}` === '#444444' ? '2px solid white' : ''
        };

        return (
            <div className="theme-selector--site-colors-wrapper">
                <div className="theme-selector--site-colors-text">{t('channel.theme-selector.site-colors')}</div>
                <div className="theme-selector--site-colors-container">
                    <div
                      className="theme-selector--color-box theme-selector--color-box-default"
                      onClick={this.handleOnClickDefault.bind(this)}
                      style={style}
                    />
                    {siteColors}
                </div>
                <div className="theme-selector--site-colors-text">{t('channel.theme-selector.choose-your-color')}</div>
            </div>
        );
    }

    renderColorWheel() {
        const style = {
            left: this.state.selectorPosition.left,
            top: this.state.selectorPosition.top
        };
        return (
            <div className="theme-selector--canvas-wrapper">
                <div className="theme-selector--canvas-selector" onClick={this.handleOnClickColorWheel.bind(this)} style={style} />
                <canvas className="theme-selector--canvas" ref="canvas" width="230" height="230" onClick={this.handleOnClickColorWheel.bind(this)} />
            </div>
        );
    }
    renderShadeBoxes() {
        return (
            <div className="theme-selector--shade-wrapper">
                {this.shadeBoxes()}
            </div>
        );
    }
    // Creation of the shade boxes. These are the divs directly below the colorWheel that modify the darkness/lightness of a color
    shadeBoxes() {
        const greyScale = [];
        for (let i = 1; i < 10; i++) {
            const style = { backgroundColor: themeSelectorHelper.greyScaleBoxBackgroundColor(i, this.state.baseColor.red, this.state.baseColor.green, this.state.baseColor.blue)};
            greyScale.push(
                <div
                  id={`shade${i}`}
                  key={`shade${i}`}
                  className={'theme-selector--grey-scale-box'}
                  onClick={this.handleOnClickColorBoxes.bind(this)}
                  style={style}
                />
            );
        }
        return greyScale;
    }

    renderCurrentColor() {
        const {t} = this.props;
        const baseTenRed = parseInt(this.state.displayColor.red, 16);
        const baseTenGreen = parseInt(this.state.displayColor.green, 16);
        const baseTenBlue = parseInt(this.state.displayColor.blue, 16);

        const divStyle = {
            backgroundColor: `#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}`,
            border: `2px solid rgb(${Math.floor(0.75 * baseTenRed)},${Math.floor(0.75 * baseTenGreen)},${Math.floor(0.75 * baseTenBlue)})`
        };
        return (
            <div className="theme-selector--current-color-wrapper">
                <div className="theme-selector-current-color-text">{t('channel.theme-selector.current')}</div>
                <div className="theme-selector--color-box theme-selector--current-color-box" style={divStyle}></div>
                <div className="theme-selector--hex-input-container">
                    <input
                      type="text"
                      value={this.state.hexInput}
                      onKeyDown={this.hexInputOnKeyHandler.bind(this)}
                      onChange={this.onHexFieldEnter.bind(this)}
                      className="theme-selector--hex-input"
                      maxLength="7"
                      onBlur={() => { this.onHexFieldResolve(); }}
                    />
                </div>
            </div>
        );
    }

    renderSavedColors() {
        const {t} = this.props;
        return (
            <div className="theme-selector--saved-colors-wrapper">
                <div className="theme-selector--saved-colors-text">{t('channel.theme-selector.my-colors')}</div>
                <div className="theme-selector--saved-colors-container">
                    <div className="theme-selector--saved-colors-button" onClick={this.updateSavedColors.bind(this)}>
                        <i className="fa fa-plus-square fa-2x" aria-hidden="true" /></div>
                    {this.savedColorsBoxes()}
                </div>
            </div>
        );
    }
    // Creation of the saved color boxes. Note that numberOfSavedColors should never be bigger than maxNumberOfSavedColors
    savedColorsBoxes() {
        const savedColors = [];
        for (let i = 0; i < Object.keys(this.state.savedColorsArr).length; i++) {
            const baseTenRed = parseInt(this.state.savedColorsArr[i].red, 16);
            const baseTenGreen = parseInt(this.state.savedColorsArr[i].green, 16);
            const baseTenBlue = parseInt(this.state.savedColorsArr[i].blue, 16);
            let border = `2px solid rgb(${Math.floor(0.75 * baseTenRed)},${Math.floor(0.75 * baseTenGreen)},${Math.floor(0.75 * baseTenBlue)})`;
            let outline = '';
            // Compares the current color to the color of the current saved color and applies a border based on the outcome
            if (`#${this.state.displayColor.red}${this.state.displayColor.green}${this.state.displayColor.blue}`.toUpperCase() ===
                `#${this.state.savedColorsArr[i].red}${this.state.savedColorsArr[i].green}${this.state.savedColorsArr[i].blue}`.toUpperCase()) {
                outline = '2px solid #444444';
                border = '2px solid white';
            }

            const style = {
                backgroundColor: `#${this.state.savedColorsArr[i].red}${this.state.savedColorsArr[i].green}${this.state.savedColorsArr[i].blue}`,
                border,
                outline
            };

            savedColors.push(
                <div
                  id={`saved${i}`}
                  key={`saved${i}`}
                  className={'theme-selector--color-box savedColors'}
                  onClick={this.handleOnClickColorBoxes.bind(this)}
                  style={style}
                />
            );
        }
        return savedColors;
    }
    renderFooter() {
        return (
            <div className="theme-selector--footer">
                <div
                  className="theme-selector--add-banner"
                  onClick={() => {
                      this.props.clickBanner();
                  }}
                >{this.footerBanner()}</div>
            </div>
        );
    }
    // JSX code for the Banner Add and Remove at the bottom of the UI
    footerBanner() {
        const {t} = this.props;
        // const self = this;
        return !this.props.banner ?
            <div className="theme-selector--banner-button">
                <span><i className="togo-icon togo-icon-picture togo-icon-lg" /></span>
                <span className="theme-selector--add-banner-title">{t('channel.theme-selector.image')}</span>
            </div>
            : <div className="theme-selector--remove-banner-button">
                <span><i className="togo-icon togo-icon-picture-disabled togo-icon-lg" /></span>
                <span className="theme-selector--add-banner-title">{t('channel.theme-selector.no-image')}</span>
            </div>;
    }

    renderThemeOverlay() {
        const themePopover = (
            <Popover className="popover-theme-selector" title="">
                <div className="theme-selector-container togo-font-regular">
                    {this.renderHeader()}
                    {this.renderSiteColors()}
                    {this.renderColorWheel()}
                    {this.renderShadeBoxes()}
                    {this.renderCurrentColor()}
                    {this.renderSavedColors()}
                    {this.renderFooter()}
                </div>
            </Popover>
        );
        return (
            <OverlayTrigger
              id="themeSelectorOverlay"
              trigger="click"
              rootClose
              placement="bottom"
              overlay={themePopover}
              onEnter={() => {
                  this.onOverlayEnter();
                  this.loadCanvasImage();
              }}
            >
                <span className="theme-icon" id="theme-selector"><i className="togo-icon togo-icon-paint-brush brush-adjust" /></span>
            </OverlayTrigger>
        );
    }


    render() {
        if (this.props.show) {
            return (this.renderThemeOverlay());
        }
        return null;
    }
}

ThemeSelector.propTypes = {
    t: PropTypes.func,
    clickBanner: PropTypes.func,
    clickTheme: PropTypes.func,
    initialColor: PropTypes.string,
    banner: PropTypes.string,
    show: PropTypes.bool
};

export default(translate(['translation'])(ThemeSelector));



// WEBPACK FOOTER //
// ./src/components/SharedComponents/themeSelector.js